import { lazy, type ReactNode, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import {
  AdminGuard,
  AdminLayout,
  AllFeaturesPage,
  AllUsersPage,
  AllVesselsPage,
  AudiencesPage,
} from '@/admin';
import { Layout, ProtectedRoute } from '@/components';
import { EditPhotoPage } from '@/pages/MyProfilePage/EditPhotoPage/EditPhotoPage';
import PaymentReturn from '@/pages/PaymentReturn/PaymentReturn';
import SettingsPage from '@/pages/SettingsPage';
import { type Permission, PERMISSIONS } from '@/permissions/permissions.catalog';
import {
  BUDGET_RIGHTS,
  CATALOGUE_RIGHTS,
  PROJECTS_RIGHTS,
  LOGBOOK_RIGHTS,
  MAINTAIN_RIGHTS,
  TRANSACTION_RIGHTS,
  MANAGE_RIGHTS,
} from '@/permissions/route-rights';
import { UserRole } from '@/utils/roles';
import { EXTRACTED_EQUIPMENT_PATH } from '@pages/Equipment/equipmentRoutes.ts';
import TransactionsPage from '@pages/Expenses/TransactionsPage/TransactionsPage.tsx';
import DailyLog from '@pages/LogbookPage/DailyLog/DailyLog';
import LogbookPage from '@pages/LogbookPage/LogbookPage.tsx';
import LogsIndexPage from '@pages/LogbookPage/LogsIndexPage.tsx';
import NavigationLog from '@pages/LogbookPage/NavigationLog/NavigationLog';
import OilLog from '@pages/LogbookPage/OilLog/OilLog';
import SafetyLog from '@pages/LogbookPage/SafetyLog/SafetyLog';
import TrashLog from '@pages/LogbookPage/TrashLog/TrashLog';

import { getAIRoutes, getDesignSystemRoutes } from './ai-routes';
import { AuthRoute } from './AuthRoute';
import { OnboardingRoute } from './OnboardingRoute';
import { getRouterRoutes } from './routes.config';

/* Every page behind `lazy()`: the app shell must not carry 57 screens in its
   first chunk. Direct paths, not the `@/pages` barrel — a dynamic import of the
   barrel pulls every page into one chunk and the split buys nothing. */
const BudgetDetail = lazy(() => import('@/pages/BudgetDetail/BudgetDetail'));
const ChatAttachmentsPage = lazy(() => import('@/pages/ChatPage/ChatAttachmentsPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage/ChatPage'));
const ChefEventPage = lazy(() => import('@/pages/Chef/ChefEventPage'));
const ConfirmInvitationPage = lazy(
  () => import('@/pages/ConfirmInvitationPage/ConfirmInvitationPage'),
);
const Crew = lazy(() => import('@/pages/Crew/Crew'));
const CrewCreateCv = lazy(() => import('@/pages/Crew/CrewCreateCv'));
const CrewManagement = lazy(() => import('@/pages/Crew/CrewManagement'));
const CrewMemberPage = lazy(() => import('@/pages/Crew/CrewMemberPage'));
const DetailedTransaction = lazy(
  () => import('@/pages/AccountingPage/components/DetailedTransaction'),
);
const Discover = lazy(() => import('@/pages/Discover/Discover'));
const DiscoverPage = lazy(() => import('@/pages/Discover/DiscoverPage'));
const JobPostingsPage = lazy(() => import('@/pages/JobPostings/JobPostingsPage'));
const JobPostingPage = lazy(() => import('@/pages/JobPostings/JobPostingPage'));
const DockageCatalogue = lazy(
  () => import('@/pages/DockagePage/DockageCatalogue/DockageCatalogue'),
);
const EditEquipment = lazy(() => import('@/pages/Equipment/EditEquipment'));
const EquipmentSingleCategory = lazy(() => import('@/pages/Equipment/EquipmentSingleCategory'));
const EventInfoPage = lazy(() => import('@/pages/EventsPage/EventInfoPage/EventInfoPage'));
const EventPreviewPage = lazy(() => import('@/pages/EventPreviewPage/EventPreviewPage'));
const EventProject = lazy(() => import('@/pages/EventProjectsPage/EventProjectsPage'));
const EventProjectDetails = lazy(() => import('@/pages/EventProjectsPage/EventProjectPage'));
const FaqPage = lazy(() => import('@/pages/FaqPage/FaqPage'));
const ForgotPassword = lazy(() => import('@/pages/Auth/ForgotPassword'));
const GuestProfileInvitation = lazy(() => import('@/pages/GuestProfile/GuestProfileInvitation'));
const GuestProfileWrapper = lazy(() => import('@/pages/GuestProfile/GuestProfileWrapper'));
const ImportedTransactionsPage = lazy(
  () => import('@pages/Expenses/TransactionsPage/blocks/ImportedTransactionsPage'),
);
const InvoicesPage = lazy(() => import('@pages/Expenses/InvoicesPage/InvoicesPage'));
const MagicLinkCallback = lazy(() => import('@/pages/Auth/MagicLinkCallback'));
const Maintain = lazy(() => import('@/pages/Maintain/Maintain'));
const MaintainPage = lazy(() => import('@/pages/Maintain/MaintainPage'));
const MaintenanceBid = lazy(() => import('@/pages/MaintenancePage/MaintenanceBid'));
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage/MaintenancePage'));
const Manage = lazy(() => import('@/pages/Manage/Manage'));
const ManagePage = lazy(() => import('@/pages/Manage/ManagePage'));
const MyYachts = lazy(() => import('@/pages/MyYachts/MyYachtsPage'));
const MyServiceDetailPage = lazy(
  () => import('@/pages/ServiceProviderProfile/MyServiceDetailPage'),
);
const MyServicesPage = lazy(() => import('@/pages/ServiceProviderProfile/MyServicesPage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage/NotificationsPage'));
const OAuthCallback = lazy(() => import('@/pages/Auth/OAuthCallback'));
const PageNotFound = lazy(() => import('@/pages/PageNotFound/PageNotFound'));
const Plan = lazy(() => import('@/pages/Plan/Plan'));
const PlanPage = lazy(() => import('@/pages/Plan/PlanPage'));
const QuotePage = lazy(() => import('@pages/Expenses/Quote/QuotePage'));
const Registration = lazy(() => import('@/pages/Auth/Registration'));
const SafetyChecklist = lazy(() => import('@/pages/SafetyChecklist/SafetyChecklist'));
const ServiceHistory = lazy(() => import('@/pages/ServiceHistory/ServiceHistory'));
const ServiceProvidersPage = lazy(() => import('@/pages/ServiceProviders/ServiceProvidersPage'));
const SingleCrew = lazy(() => import('@/pages/Crew/SingleCrew'));
const SingleDockage = lazy(() => import('@/pages/DockagePage/SingleDockage/SingleDockage'));
const SingleEquipment = lazy(() => import('@/pages/Equipment/SingleEquipment'));
const SingleServiceHistory = lazy(() => import('@/pages/ServiceHistory/SingleServiceHistory'));
const SingleServiceProviderPage = lazy(
  () => import('@/pages/ServiceProviders/SingleServiceProviderPage'),
);
const SpendingCategoryPage = lazy(
  () => import('@pages/Expenses/Spending/SpendingCategory/SpendingCategoryPage'),
);
const SuggestedMaintenances = lazy(() => import('@/pages/MaintenancePage/SuggestedMaintenances'));
const TripDetailPage = lazy(() => import('@/pages/Plan/TripDetailPage'));
const TripsPage = lazy(() => import('@/pages/Plan/TripsPage'));
const VendorMappingsPage = lazy(() => import('@pages/Expenses/VendorMappings/VendorMappingsPage'));
const VesselDetailPage = lazy(() => import('@/pages/Vessels/VesselDetailPage'));
const VesselExtractedCategories = lazy(() => import('@/pages/Equipment/VesselExtractedCategories'));
const VesselsPage = lazy(() => import('@/pages/Vessels/VesselsPage'));
const Wallet = lazy(() => import('@pages/Expenses/TransactionsPage/TransactionsPage'));

/**
 * A permission guard for the pages mounted here rather than through `getRouterRoutes()`.
 *
 * Their nav entries in `routes.config` are marked `navOnly`, so the `permission` there only
 * decorates the sidebar link — it never reached the route. The URL therefore opened for
 * anyone who typed or deep-linked it, and the service answered 403 once the page fetched.
 *
 * `title` is what the owner's plan placeholder calls the page, so a route the plan withholds
 * explains itself instead of bouncing to the dashboard.
 */
const guarded = (
  title: string,
  required: { permission?: Permission; anyOf?: readonly Permission[] },
  element: ReactNode,
): ReactNode => (
  <ProtectedRoute
    requirePermission={required.permission}
    requireAnyPermission={required.anyOf}
    lockTitle={title}
  >
    {element}
  </ProtectedRoute>
);

/* Routes outside <Layout> have no shell to hold them, so each carries its own
   boundary while its chunk lands. */
const standalone = (node: ReactNode) => <Suspense fallback={null}>{node}</Suspense>;

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },

      // ── Navigable top-level routes (source of truth: routes.config.tsx) ──
      ...getRouterRoutes(),

      // ── Sub-routes and detail pages ───────────────────────────────────────
      {
        path: 'dashboard/settings',
        element: <Navigate to="/settings" replace />,
      },
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/manage',
        element: (
          <ProtectedRoute>
            <Manage />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/manage',
            element: guarded('Manage', { anyOf: MANAGE_RIGHTS }, <ManagePage />),
            index: true,
          },
          {
            path: 'projects',
            element: guarded('Projects', { anyOf: PROJECTS_RIGHTS }, <EventProject />),
            index: true,
          },
          {
            path: 'projects/:projectId',
            element: guarded('Projects', { anyOf: PROJECTS_RIGHTS }, <EventProjectDetails />),
          },
          {
            path: 'budget-details',
            element: guarded('Budget Details', { anyOf: BUDGET_RIGHTS }, <BudgetDetail />),
            children: [
              {
                path: ':categoryId',
                element: guarded('Budget Details', { anyOf: BUDGET_RIGHTS }, <BudgetDetail />),
              },
            ],
          },
          {
            path: 'invoices',
            element: guarded(
              'Invoices',
              { permission: PERMISSIONS.wallet.Invoices },
              <InvoicesPage />,
            ),
          },
          {
            path: 'transactions',
            element: guarded('Transactions', { anyOf: TRANSACTION_RIGHTS }, <TransactionsPage />),
          },
          {
            path: 'transactions/imported',
            element: guarded(
              'Imported Transactions',
              { permission: PERMISSIONS.wallet.Transactions },
              <ImportedTransactionsPage />,
            ),
          },
          {
            // wallet lists vendor mappings behind `transactions` and only *edits* them behind
            // `VendorMappings`, so the page opens on the lighter right the list actually needs.
            path: 'transactions/vendor-mappings',
            element: guarded(
              'Vendor Mappings',
              { permission: PERMISSIONS.wallet.Transactions },
              <VendorMappingsPage />,
            ),
          },
          // { path: 'quotes', element: <QuotePage /> },
          {
            path: 'crew',
            element: guarded(
              'Crew Management',
              { permission: PERMISSIONS.users.CrewManagement },
              <CrewManagement />,
            ),
          },
          {
            // One crew member's record. The roster's cards drill in here, which
            // is what makes a person a place: linkable, reloadable, and Back
            // returns to the roster rather than closing a panel over it.
            path: 'crew/:id',
            element: guarded(
              'Crew Management',
              { permission: PERMISSIONS.users.CrewManagement },
              <CrewMemberPage />,
            ),
          },
          {
            path: 'logs',
            element: guarded('Logbook', { anyOf: LOGBOOK_RIGHTS }, <LogbookPage />),
            children: [
              {
                // The five books, and where each one stands. The section used to
                // redirect onto one of them, which left nowhere to say that.
                index: true,
                element: <LogsIndexPage />,
              },
              {
                path: 'vessel-daily-log',
                element: guarded(
                  'Daily Log',
                  { permission: PERMISSIONS.logbook.DailyLog },
                  <DailyLog />,
                ),
              },
              {
                path: 'daily-navigation-log',
                element: guarded(
                  'Daily Navigation Log',
                  { permission: PERMISSIONS.logbook.DigitalNavigationLog },
                  <NavigationLog />,
                ),
              },
              {
                path: 'trash-log',
                element: guarded(
                  'Trash Log',
                  { permission: PERMISSIONS.logbook.TrashLog },
                  <TrashLog />,
                ),
              },
              {
                path: 'safety-log',
                element: guarded(
                  'Safety Log',
                  { permission: PERMISSIONS.logbook.SafetyLog },
                  <SafetyLog />,
                ),
              },
              {
                path: 'oil-log',
                element: guarded('Oil Log', { permission: PERMISSIONS.logbook.OilLog }, <OilLog />),
              },
            ],
          },
        ],
      },
      {
        path: '/plan',
        element: (
          <ProtectedRoute>
            <Plan />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/plan',
            element: <PlanPage />,
            index: true,
          },
          { path: 'trips', element: <TripsPage /> },
        ],
      },
      {
        // Trip detail sits outside the Plan layout so it can own its own header
        // and breadcrumb; the sidebar still highlights Plan via the `/plan/*`
        // startsWith check in Header.
        path: '/plan/trips/:id',
        element: (
          <ProtectedRoute>
            <TripDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/maintain',
        element: (
          <ProtectedRoute>
            <Maintain />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/maintain',
            element: guarded('Maintain', { anyOf: MAINTAIN_RIGHTS }, <MaintainPage />),
            index: true,
          },
          {
            path: 'schedule',
            element: guarded(
              'Maintenance',
              { permission: PERMISSIONS.activity.Maintenance },
              <MaintenancePage embedded />,
            ),
          },
          {
            path: 'service-history',
            element: guarded(
              'Service History',
              { permission: PERMISSIONS.activity.Maintenance },
              <ServiceHistory />,
            ),
          },
          // Safety is no longer a Maintain section — it lives on its own at
          // /safety-checklist, so the old in-section paths just point there.
          { path: 'safety', element: <Navigate to="/safety-checklist" replace /> },
          { path: 'emergency-plan', element: <Navigate to="/safety-checklist" replace /> },

          // The category grid IS the Maintain root, so /maintain/equipment only
          // exists as the base the drill-downs hang off.
          { path: 'equipment', element: <Navigate to="/maintain" replace /> },
          {
            path: 'equipment/vessels-extracted',
            element: (
              <ProtectedRoute requirePermission={PERMISSIONS.vessel.Equipment}>
                <VesselExtractedCategories />
              </ProtectedRoute>
            ),
          },
          {
            path: 'equipment/:categoryId',
            element: (
              <ProtectedRoute requirePermission={PERMISSIONS.vessel.Equipment}>
                <EquipmentSingleCategory />
              </ProtectedRoute>
            ),
          },
          // Adding is a modal over the category now. Kept ahead of the item
          // route so an old bookmark lands on the list, not on an item lookup
          // for the id "create".
          {
            path: 'equipment/:categoryId/create',
            element: <Navigate to=".." relative="path" replace />,
          },
          {
            path: 'equipment/:categoryId/:itemId',
            element: (
              <ProtectedRoute requirePermission={PERMISSIONS.vessel.Equipment}>
                <SingleEquipment />
              </ProtectedRoute>
            ),
          },
          {
            path: 'equipment/:categoryId/:itemId/edit',
            element: (
              <ProtectedRoute requirePermission={PERMISSIONS.vessel.Equipment}>
                <EditEquipment />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '/discover',
        element: (
          <ProtectedRoute>
            <Discover />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/discover',
            element: <DiscoverPage />,
            index: true,
          },
          {
            path: 'providers',
            element: guarded(
              'Service Providers',
              { anyOf: CATALOGUE_RIGHTS },
              <ServiceProvidersPage />,
            ),
          },
          {
            path: 'marinas',
            element: guarded('Marina services', { anyOf: CATALOGUE_RIGHTS }, <DockageCatalogue />),
          },
          { path: 'crew', element: <Crew /> },
          { path: 'vacancies', element: <JobPostingsPage /> },
          { path: 'vacancies/:id', element: <JobPostingPage /> },
        ],
      },
      {
        path: 'vessels',
        element: (
          <ProtectedRoute>
            <VesselsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'vessels/:slug',
        element: (
          <ProtectedRoute>
            <VesselDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-services',
        element: (
          <ProtectedRoute>
            <MyServicesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-services/:id',
        element: (
          <ProtectedRoute>
            <MyServiceDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/inbox',
        element: (
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        ),
      },
      // The Contacts view of the same page; the static segment outranks :chatId.
      {
        path: '/inbox/contacts',
        element: (
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/inbox/contacts/:personId',
        element: (
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/inbox/:chatId',
        element: (
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/inbox/:chatId/attachments',
        element: (
          <ProtectedRoute>
            <ChatAttachmentsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'task/:id',
        element: (
          <ProtectedRoute>
            <EventInfoPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'maintenance/:id',
        element: (
          <ProtectedRoute>
            <MaintenanceBid />
          </ProtectedRoute>
        ),
      },
      {
        path: '/maintenance/suggestions',
        element: (
          <ProtectedRoute>
            <SuggestedMaintenances />
          </ProtectedRoute>
        ),
      },
      {
        path: 'expenses',
        // `TransactionsBasic` alone was stricter than wallet itself, which accepts either
        // right on the transaction list — it left a Navigate vessel unable to open a page
        // the service would have served.
        element: guarded('Transactions', { anyOf: TRANSACTION_RIGHTS }, <Wallet />),
      },
      {
        path: 'dockage/:id',
        element: (
          <ProtectedRoute>
            <SingleDockage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'spending/:name',
        element: (
          <ProtectedRoute requirePermission={PERMISSIONS.wallet.Transactions}>
            <SpendingCategoryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'invoices',
        element: (
          <ProtectedRoute requirePermission={PERMISSIONS.wallet.Invoices}>
            <InvoicesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'quote',
        element: (
          <ProtectedRoute requirePermission={PERMISSIONS.wallet.Quotes}>
            <QuotePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'imported-transactions',
        element: (
          <ProtectedRoute requirePermission={PERMISSIONS.wallet.Transactions}>
            <ImportedTransactionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'spending/vendor-mappings',
        element: (
          <ProtectedRoute requirePermission={PERMISSIONS.wallet.Transactions}>
            <VendorMappingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'accounting/:id',
        element: (
          <ProtectedRoute
            requirePermission={PERMISSIONS['service-provider'].ServiceProviderInternal}
          >
            <DetailedTransaction />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-yachts',
        element: (
          <ProtectedRoute>
            <MyYachts />
          </ProtectedRoute>
        ),
      },
      {
        path: 'my-profile/edit-photo',
        element: (
          <ProtectedRoute>
            <EditPhotoPage />
          </ProtectedRoute>
        ),
      },
      // One row of the Maintain history list, opened. Under that list's path but
      // outside the Maintain layout: the page carries its own header and back
      // button rather than the section breadcrumb.
      {
        path: 'maintain/service-history/:id',
        element: (
          <ProtectedRoute>
            <SingleServiceHistory />
          </ProtectedRoute>
        ),
      },
      // Equipment lives under Maintain now. Kept only so bookmarks on the old
      // paths land on the grid rather than a 404; nothing in the app points here.
      {
        path: 'equipment/vessels-extracted',
        element: <Navigate to={EXTRACTED_EQUIPMENT_PATH} replace />,
      },
      { path: 'equipment/*', element: <Navigate to="/maintain" replace /> },
      {
        path: '/notifications',
        element: (
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/faq',
        element: (
          <ProtectedRoute>
            <FaqPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings/permissions',
        element: <Navigate to="/settings" replace />,
      },
      {
        path: '/service-providers/:id',
        element: <SingleServiceProviderPage />,
      },
      {
        path: '/crew/:personId',
        element: <SingleCrew />,
      },
      {
        path: '/crew/create',
        element: (
          <ProtectedRoute denyRole={UserRole.GUEST}>
            <CrewCreateCv />
          </ProtectedRoute>
        ),
      },
      {
        path: '/guests/:id',
        element: <GuestProfileWrapper />,
      },
      {
        path: '/guests/:id/invite',
        element: <GuestProfileInvitation />,
      },
      {
        path: '/invite-guest',
        element: <GuestProfileInvitation />,
      },
      {
        path: '/event-menu/:id',
        element: (
          <ProtectedRoute
            requireAnyPermission={[PERMISSIONS.menu.GetMenuInfo, PERMISSIONS.menu.EditMenu]}
          >
            <ChefEventPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'safety-checklist',
        element: <SafetyChecklist />,
      },
      {
        // Replaced by /my-services, which lists every business rather than the active one.
        path: '/my-service',
        element: <Navigate to="/my-services" replace />,
      },

      // ── Dev-only: the design-system showcase, inside the app shell ──────
      ...getDesignSystemRoutes(),
    ],
  },
  ...getAIRoutes(),
  {
    path: '/vh-admin',
    element: (
      <AdminGuard>
        <AdminLayout />
      </AdminGuard>
    ),
    children: [
      { index: true, element: <Navigate to="users" replace /> },
      { path: 'users', element: <AllUsersPage /> },
      { path: 'vessels', element: <AllVesselsPage /> },
      { path: 'features', element: <AllFeaturesPage /> },
      { path: 'audiences', element: <AudiencesPage /> },
    ],
  },
  {
    path: '/auth',
    element: <AuthRoute />,
  },
  {
    path: '/onboarding',
    element: <OnboardingRoute />,
  },
  {
    path: '/oauth/:provider/callback',
    element: standalone(<OAuthCallback />),
  },
  {
    path: '/auth/magic-link',
    element: standalone(<MagicLinkCallback />),
  },
  {
    path: '/forgot-password',
    element: standalone(<ForgotPassword />),
  },
  {
    path: '/sign-up',
    element: standalone(<Registration />),
  },
  {
    path: '/confirm-invitation',
    element: standalone(<ConfirmInvitationPage />),
  },
  {
    path: '/event-preview',
    element: standalone(<EventPreviewPage />),
  },
  {
    path: '/payment/return',
    element: <PaymentReturn />,
  },
  {
    path: '*',
    element: standalone(<PageNotFound />),
  },
]);
